import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Link as RouterLink } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
} from "@material-ui/core";

import { StatusBadge } from "../../components/ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";
import {
  STAGES,
  TEMPLATE_VARIABLES,
  HOURS,
  MAX_OFFSET_DAYS,
  isOfficialChannel,
  formatHour,
  ruleToBody,
} from "./helpers";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    full: { gridColumn: "1 / -1" },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    required: { color: t.semantic.error, marginLeft: 2 },
    help: { fontSize: 12, lineHeight: 1.45, color: t.textMuted },
    placeholder: { color: t.textMuted },
    sectionTitle: {
      gridColumn: "1 / -1",
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
      margin: "8px 0 0",
      paddingTop: 12,
      borderTop: `1px solid ${t.line}`,
    },
    sectionTitleFirst: { margin: 0, paddingTop: 0, borderTop: "none" },
    stageGroup: {
      "& .MuiFormControlLabel-label": { fontSize: 14 },
      "& .MuiFormControlLabel-root": { marginRight: 20 },
    },
    variablesBox: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 6,
    },
    variableChip: {
      cursor: "pointer",
      height: 22,
      fontFamily: "monospace",
      fontSize: 11.5,
      fontWeight: 500,
      color: t.textSecondary,
      "& .MuiChip-label": { paddingLeft: 7, paddingRight: 7 },
      "&:hover, &:focus": { backgroundColor: t.muted, color: t.text },
    },
    warningBox: {
      padding: "10px 14px",
      borderRadius: t.radius.sm,
      backgroundColor: t.status.warn.bg,
      color: t.status.warn.fg,
      fontSize: 13,
      lineHeight: 1.5,
      "& a": { color: "inherit", fontWeight: 600, textDecoration: "underline" },
    },
    options: {
      gridColumn: "1 / -1",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    // rótulo + ajuda ao lado do controle, alinhados pelo topo
    option: {
      alignItems: "flex-start",
      marginRight: 0,
      "& .MuiFormControlLabel-label": { paddingTop: 9, minWidth: 0 },
    },
    optionTitle: { display: "block", fontSize: 14 },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      color: theme.palette.primary.main,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  };
});

const buildInitialForm = (rule) => {
  if (!rule) {
    return {
      name: "",
      erpIntegrationId: "",
      whatsappId: "",
      stage: "before",
      offsetDays: 3,
      sendHour: 9,
      quickMessageId: "",
      textTemplate: i18n.t("billingRules.form.defaultTemplate"),
      active: true,
      sendPdf: false,
      copyButtons: false,
      replyQueueId: "",
      filters: {},
    };
  }
  const body = ruleToBody(rule);
  return {
    ...body,
    erpIntegrationId: body.erpIntegrationId ?? "",
    whatsappId: body.whatsappId ?? "",
    replyQueueId: body.replyQueueId ?? "",
    offsetDays: body.stage === "due" ? 3 : body.offsetDays || 1,
    quickMessageId: body.quickMessageId ?? "",
    textTemplate: body.textTemplate ?? "",
  };
};

const BillingRuleModal = ({ open, onClose, rule, erpIntegrations, whatsapps, onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState(() => buildInitialForm(rule));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [queues, setQueues] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/queue").then(({ data }) => setQueues(Array.isArray(data) ? data : [])).catch(() => {});
  }, [open]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const textRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm(rule));
      setErrors({});
    }
  }, [open, rule]);

  const selectedWhatsapp = whatsapps.find((w) => String(w.id) === String(form.whatsappId));
  const official = isOfficialChannel(selectedWhatsapp);

  // Approved Meta templates for the selected official connection
  useEffect(() => {
    if (!open || !official || !form.whatsappId) {
      setTemplates([]);
      return undefined;
    }
    let active = true;
    setLoadingTemplates(true);
    (async () => {
      try {
        const { data } = await api.get("/meta-templates", {
          params: { whatsappId: form.whatsappId },
        });
        const list = Array.isArray(data?.templates) ? data.templates : [];
        if (active) {
          setTemplates(
            list.filter((tpl) => String(tpl.status || "").toUpperCase() === "APPROVED")
          );
        }
      } catch (err) {
        if (active) setTemplates([]);
        toastError(err);
      } finally {
        if (active) setLoadingTemplates(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, official, form.whatsappId]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleChange = (field) => (event) => setField(field, event.target.value);

  const insertVariable = (variable) => {
    setForm((prev) => {
      const current = prev.textTemplate || "";
      const el = textRef.current;
      if (el && typeof el.selectionStart === "number") {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        return {
          ...prev,
          textTemplate: current.slice(0, start) + variable + current.slice(end),
        };
      }
      return { ...prev, textTemplate: `${current}${current ? " " : ""}${variable}` };
    });
  };

  const validate = () => {
    const next = {};
    if (!form.name || form.name.trim().length < 2) {
      next.name = i18n.t("billingRules.validation.nameRequired");
    }
    if (!form.erpIntegrationId) next.erpIntegrationId = i18n.t("billingRules.validation.erpRequired");
    if (!form.whatsappId) next.whatsappId = i18n.t("billingRules.validation.whatsappRequired");
    if (!STAGES.includes(form.stage)) next.stage = i18n.t("billingRules.validation.stageRequired");
    if (form.stage !== "due") {
      const n = Number(form.offsetDays);
      if (!Number.isInteger(n) || n < 1 || n > MAX_OFFSET_DAYS) {
        next.offsetDays = i18n.t("billingRules.validation.offsetDaysInvalid", {
          max: MAX_OFFSET_DAYS,
        });
      }
    }
    const hour = Number(form.sendHour);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      next.sendHour = i18n.t("billingRules.validation.sendHourInvalid");
    }
    if (official) {
      if (!form.quickMessageId) {
        next.quickMessageId = i18n.t("billingRules.validation.templateRequired");
      }
    } else if (!form.textTemplate || !form.textTemplate.trim()) {
      next.textTemplate = i18n.t("billingRules.validation.textRequired");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildBody = () => ({
    name: form.name.trim(),
    erpIntegrationId: Number(form.erpIntegrationId),
    whatsappId: Number(form.whatsappId),
    stage: form.stage,
    offsetDays: form.stage === "due" ? 0 : Number(form.offsetDays),
    sendHour: Number(form.sendHour),
    quickMessageId: official ? Number(form.quickMessageId) : null,
    textTemplate: official ? null : form.textTemplate.trim(),
    active: !!form.active,
    // PDF attachment only exists on the non-official (Baileys) channel
    sendPdf: !official && !!form.sendPdf,
    copyButtons: !!form.copyButtons,
    replyQueueId: form.replyQueueId ? Number(form.replyQueueId) : null,
    filters: form.filters || {},
  });

  const handleSave = async () => {
    if (!validate()) {
      toast.error(i18n.t("billingRules.validation.fixErrors"));
      return;
    }
    setSaving(true);
    try {
      const body = buildBody();
      if (rule?.id) {
        await api.put(`/billing-rules/${rule.id}`, body);
        toast.success(i18n.t("billingRules.toasts.updated"));
      } else {
        await api.post("/billing-rules", body);
        toast.success(i18n.t("billingRules.toasts.created"));
      }
      if (!isMountedRef.current) return;
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const days = Number(form.offsetDays) || 0;
  const stagePreview =
    form.stage === "before" ? `D-${days}` : form.stage === "after" ? `D+${days}` : "D0";

  const selectedErp = erpIntegrations.find((it) => String(it.id) === String(form.erpIntegrationId));
  const placeholder = <span className={classes.placeholder}>Selecione…</span>;
  const requiredMark = (
    <span className={classes.required} aria-hidden="true">
      *
    </span>
  );
  const optionLabel = (title, help) => (
    <span>
      <span className={classes.optionTitle}>{title}</span>
      <span className={classes.help}>{help}</span>
    </span>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="billing-rule-modal-title"
    >
      <DialogTitle id="billing-rule-modal-title">
        {rule?.id ? i18n.t("billingRules.modal.editTitle") : i18n.t("billingRules.modal.newTitle")}
      </DialogTitle>
      <DialogContent>
        <div className={classes.formGrid}>
          <div className={`${classes.sectionTitle} ${classes.sectionTitleFirst}`}>Identificação</div>

          <div className={classes.field}>
            <label className={classes.label} htmlFor="billing-rule-name">
              {i18n.t("billingRules.form.name")}
              {requiredMark}
            </label>
            <TextField
              id="billing-rule-name"
              value={form.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={errors.name}
              variant="outlined"
              size="small"
              fullWidth
              required
              autoFocus
            />
          </div>

          <div className={classes.field}>
            <label className={classes.label} id="billing-rule-erp-label">
              {i18n.t("billingRules.form.erpIntegration")}
              {requiredMark}
            </label>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              required
              error={!!errors.erpIntegrationId}
            >
              <Select
                labelId="billing-rule-erp-label"
                value={form.erpIntegrationId}
                onChange={handleChange("erpIntegrationId")}
                displayEmpty
                renderValue={() =>
                  selectedErp ? `${selectedErp.name} (${selectedErp.type})` : placeholder
                }
              >
                <MenuItem value="">{i18n.t("billingRules.form.none")}</MenuItem>
                {erpIntegrations.map((it) => (
                  <MenuItem key={it.id} value={it.id}>
                    {it.name} ({it.type})
                  </MenuItem>
                ))}
              </Select>
              {errors.erpIntegrationId && (
                <FormHelperText>{errors.erpIntegrationId}</FormHelperText>
              )}
            </FormControl>
          </div>

          <div className={classes.sectionTitle}>
            {i18n.t("billingRules.form.stage")}
            <StatusBadge color="neutral" size="sm" dot={false} label={stagePreview} />
          </div>

          <div className={classes.full}>
            <FormControl component="fieldset" error={!!errors.stage} fullWidth>
              <RadioGroup
                row
                className={classes.stageGroup}
                aria-label={i18n.t("billingRules.form.stage")}
                value={form.stage}
                onChange={(e) => {
                  const stage = e.target.value;
                  setField("stage", stage);
                  if (stage !== "due" && (!Number(form.offsetDays) || Number(form.offsetDays) < 1)) {
                    setField("offsetDays", stage === "before" ? 3 : 5);
                  }
                }}
              >
                <FormControlLabel
                  value="before"
                  control={<Radio color="primary" />}
                  label={i18n.t("billingRules.form.stageBefore")}
                />
                <FormControlLabel
                  value="due"
                  control={<Radio color="primary" />}
                  label={i18n.t("billingRules.form.stageDue")}
                />
                <FormControlLabel
                  value="after"
                  control={<Radio color="primary" />}
                  label={i18n.t("billingRules.form.stageAfter")}
                />
              </RadioGroup>
              {errors.stage && <FormHelperText>{errors.stage}</FormHelperText>}
            </FormControl>
          </div>

          {form.stage !== "due" && (
            <div className={classes.field}>
              <label className={classes.label} htmlFor="billing-rule-offset-days">
                {i18n.t("billingRules.form.offsetDays")}
                {requiredMark}
              </label>
              <TextField
                id="billing-rule-offset-days"
                type="number"
                inputProps={{ min: 1, max: MAX_OFFSET_DAYS, step: 1 }}
                value={form.offsetDays}
                onChange={handleChange("offsetDays")}
                error={!!errors.offsetDays}
                helperText={errors.offsetDays}
                variant="outlined"
                size="small"
                fullWidth
                required
              />
            </div>
          )}

          <div className={classes.field}>
            <label className={classes.label} id="billing-rule-hour-label">
              {i18n.t("billingRules.form.sendHour")}
            </label>
            <FormControl variant="outlined" size="small" fullWidth error={!!errors.sendHour}>
              <Select
                labelId="billing-rule-hour-label"
                value={form.sendHour}
                onChange={handleChange("sendHour")}
              >
                {HOURS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {formatHour(h)}
                  </MenuItem>
                ))}
              </Select>
              {errors.sendHour && <FormHelperText>{errors.sendHour}</FormHelperText>}
            </FormControl>
          </div>

          <div className={classes.sectionTitle}>Canal e mensagem</div>

          <div className={classes.field}>
            <label className={classes.label} id="billing-rule-whatsapp-label">
              {i18n.t("billingRules.form.whatsapp")}
              {requiredMark}
            </label>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              required
              error={!!errors.whatsappId}
            >
              <Select
                labelId="billing-rule-whatsapp-label"
                value={form.whatsappId}
                onChange={(e) => {
                  setField("whatsappId", e.target.value);
                  setField("quickMessageId", "");
                }}
                displayEmpty
                renderValue={() =>
                  selectedWhatsapp
                    ? `${selectedWhatsapp.name}${
                        official ? ` (${i18n.t("billingRules.form.official")})` : ""
                      }`
                    : placeholder
                }
              >
                <MenuItem value="">{i18n.t("billingRules.form.none")}</MenuItem>
                {whatsapps.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                    {isOfficialChannel(w)
                      ? ` (${i18n.t("billingRules.form.official")})`
                      : ""}
                  </MenuItem>
                ))}
              </Select>
              {errors.whatsappId && <FormHelperText>{errors.whatsappId}</FormHelperText>}
            </FormControl>
          </div>

          <div className={classes.field}>
            <label className={classes.label} id="billing-rule-reply-queue-label">
              {i18n.t("billingRules.form.replyQueue")}
            </label>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                labelId="billing-rule-reply-queue-label"
                value={form.replyQueueId}
                onChange={(e) => setField("replyQueueId", e.target.value)}
                displayEmpty
              >
                <MenuItem value="">{i18n.t("billingRules.form.none")}</MenuItem>
                {queues.map((q) => (
                  <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <span className={classes.help}>{i18n.t("billingRules.form.replyQueueHelp")}</span>
          </div>

          {official ? (
            <div className={`${classes.field} ${classes.full}`}>
              <label className={classes.label} id="billing-rule-template-label">
                {i18n.t("billingRules.form.metaTemplate")}
                {requiredMark}
              </label>
              {!loadingTemplates && templates.length === 0 ? (
                <div className={classes.warningBox}>
                  {i18n.t("billingRules.form.noApprovedTemplates")}{" "}
                  <Link component={RouterLink} to="/meta-templates">
                    {i18n.t("billingRules.form.goToTemplates")}
                  </Link>
                </div>
              ) : (
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  error={!!errors.quickMessageId}
                >
                  <Select
                    labelId="billing-rule-template-label"
                    value={form.quickMessageId}
                    onChange={handleChange("quickMessageId")}
                    disabled={loadingTemplates}
                    displayEmpty
                  >
                    <MenuItem value="">{i18n.t("billingRules.form.none")}</MenuItem>
                    {templates.map((tpl) => (
                      <MenuItem key={tpl.id} value={tpl.id}>
                        {tpl.name}
                        {tpl.language ? ` (${tpl.language})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.quickMessageId ||
                      (loadingTemplates
                        ? i18n.t("billingRules.form.loadingTemplates")
                        : i18n.t("billingRules.form.metaTemplateHelp"))}
                  </FormHelperText>
                </FormControl>
              )}
            </div>
          ) : (
            <div className={`${classes.field} ${classes.full}`}>
              <label className={classes.label} htmlFor="billing-rule-text">
                {i18n.t("billingRules.form.textTemplate")}
                {requiredMark}
              </label>
              <TextField
                id="billing-rule-text"
                value={form.textTemplate}
                onChange={handleChange("textTemplate")}
                inputRef={textRef}
                error={!!errors.textTemplate}
                helperText={errors.textTemplate}
                multiline
                minRows={4}
                maxRows={10}
                variant="outlined"
                fullWidth
                required
              />
              <div className={classes.variablesBox}>
                <span className={classes.help}>{i18n.t("billingRules.form.variablesHelp")}</span>
                {TEMPLATE_VARIABLES.map((v) => (
                  <Chip
                    key={v}
                    size="small"
                    label={v}
                    variant="outlined"
                    className={classes.variableChip}
                    onClick={() => insertVariable(v)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={classes.sectionTitle}>Opções</div>

          <div className={classes.options}>
            <FormControlLabel
              className={classes.option}
              control={
                <Checkbox
                  color="primary"
                  checked={!official && !!form.sendPdf}
                  disabled={official}
                  onChange={(e) => setField("sendPdf", e.target.checked)}
                />
              }
              label={optionLabel(
                i18n.t("billingRules.form.sendPdf"),
                official
                  ? i18n.t("billingRules.form.sendPdfOfficialHelp")
                  : i18n.t("billingRules.form.sendPdfHelp")
              )}
            />
            <FormControlLabel
              className={classes.option}
              control={
                <Checkbox
                  color="primary"
                  checked={!!form.copyButtons}
                  onChange={(e) => setField("copyButtons", e.target.checked)}
                />
              }
              label={optionLabel(
                i18n.t("billingRules.form.copyButtons"),
                official
                  ? i18n.t("billingRules.form.copyButtonsOfficialHelp")
                  : i18n.t("billingRules.form.copyButtonsHelp")
              )}
            />
            <FormControlLabel
              className={classes.option}
              control={
                <Switch
                  color="primary"
                  checked={!!form.active}
                  onChange={(e) => setField("active", e.target.checked)}
                />
              }
              label={optionLabel(
                i18n.t("billingRules.form.active"),
                i18n.t("billingRules.form.activeHelp")
              )}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {i18n.t("billingRules.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
          className={classes.btnWrapper}
        >
          {rule?.id ? i18n.t("billingRules.buttons.save") : i18n.t("billingRules.buttons.create")}
          {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingRuleModal;
